/** /academy/checkout/:id — the payment's status, polled every few seconds: validate on the phone
 * (MTN MoMo), back from the provider's page, or the local simulator in test mode (D-110). */

import {
  getAcademyAccountRetrieveQueryKey,
  getAcademyMeRetrieveQueryKey,
  useAcademyCheckoutRetrieve,
  useAcademyCheckoutSimulateCreate,
} from "@agencyos/api-client";
import { chargedText, formatPhone, formatXaf } from "@agencyos/core";
import { Button, GlassCard, Skeleton } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, CircleAlert, FileText, FlaskConical, Loader2, Smartphone } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { dateText } from "../shared";

export function CheckoutPage() {
  const { t, i18n } = useTranslation();
  const { id = "" } = useParams();
  const queryClient = useQueryClient();
  const state = useAcademyCheckoutRetrieve(id, {
    query: {
      enabled: Boolean(id),
      retry: false,
      refetchInterval: (query) => {
        const data = query.state.data;
        return data?.status === 200 && data.data.status === "pending" ? 3000 : false;
      },
    },
  });
  const simulate = useAcademyCheckoutSimulateCreate();
  const data = state.data?.status === 200 ? state.data.data : undefined;
  const locale = i18n.resolvedLanguage === "en" ? "en-GB" : "fr-FR";

  useEffect(() => {
    if (data?.status === "confirmed") {
      void queryClient.invalidateQueries({ queryKey: getAcademyMeRetrieveQueryKey() });
      void queryClient.invalidateQueries({ queryKey: getAcademyAccountRetrieveQueryKey() });
    }
  }, [data?.status, queryClient]);

  const amount = data ? chargedText(data.amount_minor, data.currency, locale) : "";
  return (
    <div className="ac-page as-status-page">
      <GlassCard as="section" level="thick" className="as-status" aria-labelledby="as-status-title">
        {state.isLoading ? <Skeleton style={{ height: 180 }} /> : null}
        {state.isError ? <p className="ko">{t("common.error")}</p> : null}
        {data ? (
          <>
            <div role="status" aria-live="polite" className={`as-status__head as-status__head--${data.status}`}>
              {data.status === "confirmed" ? (
                <CheckCircle2 aria-hidden size={40} />
              ) : data.status === "pending" ? (
                data.method === "momo_mtn" ? (
                  <Smartphone aria-hidden size={40} />
                ) : (
                  <Loader2 aria-hidden size={40} className="as-spin" />
                )
              ) : (
                <CircleAlert aria-hidden size={40} />
              )}
              <h1 id="as-status-title" className="ac-title-1">
                {data.status === "confirmed" && data.booking
                  ? t("coaching.checkout.confirmed")
                  : data.status === "confirmed"
                  ? t("academySales.checkout.confirmed")
                  : data.status === "pending"
                    ? data.method === "momo_mtn"
                      ? t("academySales.checkout.pushTitle")
                      : t("academySales.checkout.pending")
                    : data.status === "cancelled"
                      ? t("academySales.checkout.cancelled")
                      : t("academySales.checkout.failed")}
              </h1>
              <p className="ac-secondary">
                {data.status === "confirmed" && data.booking
                  ? t("coaching.checkout.confirmedText")
                  : data.status === "confirmed" && data.access_until
                  ? t("academySales.checkout.confirmedText", { date: dateText(data.access_until, i18n.language) })
                  : data.status === "pending"
                    ? data.method === "momo_mtn"
                      ? t("academySales.checkout.pushText", { amount, phone: formatPhone(data.phone) })
                      : t("academySales.checkout.pendingRedirect")
                    : t("academySales.checkout.failedText")}
              </p>
              {data.status === "pending" ? <p className="ac-footnote">{t("academySales.checkout.waiting")}</p> : null}
              {/* D-132: Moneroo was unavailable; the payment went to its fallback (CinetPay) */}
              {data.failover_from ? (
                <p role="note" className="ac-footnote">
                  {t("academySales.checkout.failover", { provider: data.provider_label.split(" (")[0], from: data.failover_from })}
                </p>
              ) : null}
            </div>
            <dl className="as-total">
              {/* D-131: the provider's fee, apart from the price (the amount below is the total) */}
              {data.fee_xaf > 0 ? (
                <>
                  <div>
                    <dt>{t("academySales.plans.passPrice")}</dt>
                    <dd className="ao-num">{formatXaf(data.amount_xaf)}</dd>
                  </div>
                  <div>
                    <dt>{t("academySales.checkout.fee")}</dt>
                    <dd className="ao-num">{formatXaf(data.fee_xaf)}</dd>
                  </div>
                </>
              ) : null}
              <div>
                <dt>{data.fee_xaf > 0 ? t("academySales.checkout.totalPaid") : t("academySales.checkout.amount")}</dt>
                <dd className="ao-num">{amount}</dd>
              </div>
              <div>
                <dt>{t("academySales.checkout.method")}</dt>
                <dd>
                  {t(
                    `academySales.method.${
                      data.method === "momo_mtn"
                        ? "mtn_momo"
                        : data.method === "mobile_money" || data.method === "moneroo"
                          ? data.method
                          : "card"
                    }`,
                  )}
                </dd>
              </div>
              {/* D-128: the means chosen on Moneroo's page (e.g. MTN MoMo Cameroon), once read back */}
              {data.channel ? (
                <div>
                  <dt>{t("academySales.checkout.channel")}</dt>
                  <dd>{data.channel}</dd>
                </div>
              ) : null}
              <div>
                <dt>{t("academySales.checkout.reference")}</dt>
                <dd className="ao-num">{data.invoice_number}</dd>
              </div>
            </dl>
            {data.status === "pending" && data.simulator ? (
              <section className="as-sandbox" aria-labelledby="as-sandbox-title">
                <h2 id="as-sandbox-title" className="as-sandbox__title">
                  <FlaskConical aria-hidden size={18} /> {t("academySales.checkout.sandboxTitle")}
                </h2>
                <p className="ac-footnote">{t("academySales.checkout.sandboxText", { provider: data.provider_label })}</p>
                <div className="as-sandbox__actions">
                  <Button
                    disabled={simulate.isPending}
                    onClick={() => simulate.mutate({ id, data: { success: true } }, { onSuccess: () => void state.refetch() })}
                  >
                    {t("academySales.checkout.simulateOk")}
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={simulate.isPending}
                    onClick={() => simulate.mutate({ id, data: { success: false } }, { onSuccess: () => void state.refetch() })}
                  >
                    {t("academySales.checkout.simulateKo")}
                  </Button>
                </div>
              </section>
            ) : null}
            <div className="as-status__actions">
              {data.status === "confirmed" ? (
                <>
                  {data.booking ? (
                    <Link className="ao-button ao-button--primary" to="/academy/oral">
                      {t("coaching.checkout.see")}
                    </Link>
                  ) : (
                    <Link className="ao-button ao-button--primary" to="/academy/mocks">
                      {t("academySales.checkout.start")}
                    </Link>
                  )}
                  {data.invoice_url ? (
                    <a className="ac-link" href={data.invoice_url} target="_blank" rel="noreferrer">
                      <FileText aria-hidden size={16} /> {t("academySales.checkout.invoice", { number: data.invoice_number })}
                    </a>
                  ) : null}
                  {data.receipt_number ? (
                    <p className="ac-footnote">{t("academySales.checkout.receipt", { number: data.receipt_number })}</p>
                  ) : null}
                </>
              ) : null}
              {data.status === "failed" || data.status === "cancelled" ? (
                <Link className="ao-button ao-button--primary" to={data.booking ? "/academy/oral" : "/academy/plans"}>
                  {data.booking ? t("coaching.checkout.retry") : t("academySales.checkout.retry")}
                </Link>
              ) : null}
              <Link className="ac-link" to="/academy/home">
                {t("academySales.checkout.back")}
              </Link>
            </div>
            {data.is_test ? <p className="ac-footnote as-test">{t("academySales.plans.test")}</p> : null}
          </>
        ) : null}
      </GlassCard>
    </div>
  );
}
